import {Component, ElementRef, EventEmitter, Input, Output, ViewChild} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';

@Component({
    selector: 'app-input',
    templateUrl: './input.component.html',
    styleUrl: './input.component.scss',
    standalone: true,
    imports: [
        CommonModule,
        TranslateModule
    ]
})

export class InputComponent {
    id: number = Math.floor(Math.random() * 9999999) + 1 //NOSONAR
    dataListId: string = 'datalist-' + Math.floor(Math.random() * 9999999) + 1 //NOSONAR

    @Output()
    valueChanged: EventEmitter<any> = new EventEmitter<any>()

    @Output()
    inputChanged: EventEmitter<any> = new EventEmitter<any>()

    @Output()
    keyPressed: EventEmitter<any> = new EventEmitter<any>()

    @Output()
    enterPressed: EventEmitter<any> = new EventEmitter<any>()

    @ViewChild('input')
    input: ElementRef

    @Input('maxDecimals')
    maxDecimals: number = 2

    @Input('type')
    type: string

    @Input('value')
    value: any = ''

    @Input('placeholder')
    placeholder: string | null = ''

    @Input('label')
    label: string

    @Input('suggestions')
    suggestions: string[] = []

    @Input('class')
    class: string = ''

    @Input('divClass')
    divClass: string = ''

    @Input('block')
    block: boolean = false

    @Input('translate')
    translate: boolean = false

    @Input('min')
    min: any = 0.01

    @Input('max')
    max: any = null

    @Input('maxLength')
    maxLength: number | null = null

    @Input('disabled')
    disabled = false

    @Input('required')
    required = false

    @Input('dirty')
    dirty = false

    @Input('showArrows')
    showArrows: boolean = true

    @Input('step')
    step: number | null = null

    private typingTimer: any = null

    /**
     * Método que se ejecuta al hacer foco en el input
     */
    onFocus(event: any): void {
        // Para inputs numéricos, limpiamos el valor "0" para facilitar la entrada
        if (this.isNumberType() && event.target.value === '0') {
            event.target.value = '' // Limpiar el input si el valor es 0
        }
    }

    /**
     * Método que se ejecuta al perder el foco en el input
     */
    onBlur(event: any): void {
        if (this.isNumberType()) {
            const value = event.target.value;
            
            // Si está vacío, establecer valor por defecto
            if (value === '') {
                event.target.value = '0';
                this.value = 0;
                return;
            }

            // Formatea el número según las reglas (max decimales)
            const formattedValue = this.formatNumberValue(value);
            
            // Actualiza el valor en el input y el modelo
            event.target.value = formattedValue;
            this.value = this.parseNumberValue(formattedValue);
            this.valueChanged.emit(this.value);
        }
    }

    /**
     * Maneja los cambios en el input
     */
    onChange(event: any) {
        this.dirty = true;

        let newValue = event.target.value;
        
        if (this.isNumberType()) {
            // Convertir a número para las comparaciones
            const numValue = this.parseNumberValue(newValue);
            
            // Validar límites min/max
            if (numValue < this.min) {
                const defaultValue = this.min === 0 ? 0 : this.min;
                event.target.value = String(defaultValue);
                this.value = defaultValue;
                this.valueChanged.emit(defaultValue);
                return;
            }

            if (this.max !== null && numValue > this.max) {
                event.target.value = String(this.max);
                this.value = this.max;
                this.valueChanged.emit(this.max);
                return;
            }

            // Formatear decimales si es necesario
            newValue = this.formatNumberValue(newValue);
            event.target.value = newValue;
            
            // Emitir el valor como número, no como string
            this.valueChanged.emit(this.parseNumberValue(newValue));
        } else {
            // Para tipos no numéricos, emitir el valor tal cual
            this.valueChanged.emit(newValue);
        }

        // Manejo de sugerencias
        if (newValue && this.suggestions.includes(newValue)) {
            setTimeout(() => {
                this.input.nativeElement.blur();
            }, 100);
        }
    }

    /**
     * Maneja el evento keydown para prevenir entradas inválidas en campos numéricos
     */
    onKeyDown(event: any) {
        // Permitir teclas de navegación y edición (flechas, borrar, etc.)
        const navigationalKeys = ['ArrowLeft', 'ArrowRight', 'Backspace', 'Delete', 'Tab', 'Enter', 'Home', 'End'];
        if(event.key === 'Enter') {
            this.input.nativeElement.blur()
            this.enterPressed.emit(event.target.value);
        }
        if (navigationalKeys.includes(event.key)) {
            this.keyPressed.emit(event.target.value);
            return;
        }

        // Para campos numéricos, validar el input
        if (this.isNumberType()) {
            const allowedKeys = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];
            
            // Permitir signo negativo al principio si min < 0
            if (event.key === '-' && this.min < 0 && event.target.selectionStart === 0 && !event.target.value.includes('-')) {
                this.keyPressed.emit(event.target.value);
                return;
            }
            
            // Permitir punto decimal si no hay ya uno y maxDecimals > 0
            if (event.key === '.' && this.maxDecimals > 0 && !event.target.value.includes('.')) {
                this.keyPressed.emit(event.target.value);
                return;
            }
            
            // Prevenir entradas inválidas para números
            if (!allowedKeys.includes(event.key) && !event.ctrlKey && !event.metaKey) {
                event.preventDefault();
                return;
            }
        }
        
        this.keyPressed.emit(event.target.value);
    }

    onFocusOut(event: any) {
        this.dirty = true;
    }

    /**
     * Verifica si el valor es válido según las reglas del componente
     */
    isValueValid(value: string): boolean {
        if (this.isNumberType()) {
            if (!value || value === '') return false;
            
            const numValue = parseFloat(value);
            if (isNaN(numValue)) return false;
            
            // Verificar límites min/max
            if (numValue < this.min) return false;
            if (this.max !== null && numValue > this.max) return false;
            
            return true;
        }
        return !!value;
    }

    /**
     * Maneja cambios en tiempo real al escribir en el input
     */
    onInput(event: any): void {
        if (this.typingTimer) clearTimeout(this.typingTimer);
        this.typingTimer = setTimeout(() => { this.dirty = true; }, 800);

        let newValue = event.target.value;
        
        if (this.isNumberType()) {
            // Para los campos numéricos, intentamos mantener la coherencia
            // pero sin formatear mientras el usuario escribe (para no mover el cursor)
            
            // Solo validamos formato básico sin cambiar el valor
            if (newValue && !this.isValidNumberFormat(newValue)) {
                // Si el formato es inválido, no actualizamos ni emitimos
                return;
            }
            
            // Emitir el valor en su formato actual (string) durante la edición
            this.inputChanged.emit(newValue);
            this.value = newValue; // Mantener valor como string durante edición
        } else {
            // Para campos no numéricos
            this.inputChanged.emit(newValue);
            this.value = newValue;
        }
    }

    /**
     * Verifica si el input es de tipo numérico
     */
    public isNumberType(): boolean {
        return this.type === 'number';
    }

    /**
     * Convierte un valor string a número
     */
    public parseNumberValue(value: string): number {
        return parseFloat(value) || 0;
    }

    /**
     * Formatea el valor numérico según las reglas especificadas
     */
    private formatNumberValue(value: string): string {
        if (!value || value === '') return '0';
        
        // Convertir a número y redondear
        const numValue = parseFloat(value);
        if (isNaN(numValue)) return '0';
        
        // Usar toFixed para redondear correctamente
        return numValue.toFixed(this.maxDecimals);
    }

    /**
     * Valida si el formato del número es correcto mientras se edita
     */
    private isValidNumberFormat(value: string): boolean {
        // Expresión regular para validar un número en proceso de edición
        // Permite: dígitos, un signo negativo al inicio y un punto decimal
        const numberRegex = /^-?\d*\.?\d*$/; //NOSONAR
        return numberRegex.test(value);
    }

    /**
     * Obtiene el valor de incremento/decremento
     */
    private getStepValue(): number {
        if (this.step !== null) {
            return this.step;
        }
        return this.maxDecimals > 0 ? Math.pow(0.1, this.maxDecimals) : 1;
    }

    /**
     * Incrementa el valor del input numérico
     */
    incrementValue(inputElement: HTMLInputElement): void {
        if (this.disabled) return;
        
        const currentValue = this.parseNumberValue(inputElement.value);
        const step = this.getStepValue();
        
        let newValue = currentValue + step;
        
        // Verificar límite máximo
        if (this.max !== null && newValue > this.max) {
            newValue = this.max;
        }
        
        // Formatear y actualizar
        inputElement.value = this.formatNumberValue(String(newValue));
        this.onChange({ target: inputElement });
    }
    
    /**
     * Decrementa el valor del input numérico
     */
    decrementValue(inputElement: HTMLInputElement): void {
        if (this.disabled) return;
        
        const currentValue = this.parseNumberValue(inputElement.value);
        const step = this.getStepValue();
        
        let newValue = currentValue - step;
        
        // Verificar límite mínimo
        if (newValue < this.min) {
            newValue = this.min;
        }
        
        // Formatear y actualizar
        inputElement.value = this.formatNumberValue(String(newValue));
        this.onChange({ target: inputElement });
    }
}
